/**
 * CommunityModerationDashboard - Admin panel for moderating community posts
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Textarea } from '../../components/ui/textarea';
import { 
  Shield, AlertTriangle, CheckCircle, XCircle, Flag,
  Clock, Eye, Search, Filter, RefreshCw, Loader2,
  Users, GraduationCap, Car, HeartHandshake, Gift,
  Megaphone, HelpingHand, Phone, Mail, MapPin,
  ChevronRight, BarChart3, TrendingUp
} from 'lucide-react';
import { useCountry } from '../../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icons
const CATEGORY_ICONS = {
  'local-groups-clubs': Users,
  'classes-workshops': GraduationCap,
  'lost-found': Search,
  'volunteers': HeartHandshake,
  'charity-donations': Gift,
  'notices-announcements': Megaphone,
  'ride-sharing': Car,
  'community-help': HelpingHand,
};

// Risk level colors
const RISK_COLORS = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-red-100 text-red-800'
};

// Status colors
const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  active: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  flagged: 'bg-orange-100 text-orange-800'
};

// Stats Card Component
const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
  <Card>
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtext && <p className="text-xs text-gray-400">{subtext}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Post Card for Moderation
const ModerationPostCard = ({ post, onAction, loading }) => {
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const Icon = CATEGORY_ICONS[post.category] || Shield;
  
  return (
    <Card className="mb-4" data-testid={`mod-post-${post.id}`}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Post Info */}
          <div className="flex-1">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon className="w-5 h-5 text-gray-500" />
                <Badge variant="outline">{post.category}</Badge>
                <Badge className={RISK_COLORS[post.risk_level]}>
                  {post.risk_level} risk
                </Badge>
              </div>
              <span className="text-xs text-gray-400">
                {new Date(post.created_at).toLocaleString()}
              </span>
            </div>
            
            <h3 className="font-bold text-lg mb-1">{post.title}</h3>
            <p className="text-gray-600 text-sm line-clamp-2 mb-2">{post.description}</p>
            
            <div className="flex items-center gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {post.location}
              </span>
              {post.contact_phone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {post.contact_phone}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {post.views || 0} views
              </span>
              {post.reports_count > 0 && (
                <span className="flex items-center gap-1 text-red-500">
                  <Flag className="w-3 h-3" />
                  {post.reports_count} reports
                </span>
              )}
            </div>
            
            {/* Risk Factors */}
            {post.risk_factors && post.risk_factors.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {post.risk_factors.map((factor, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs bg-red-50">
                    {factor}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="w-64 space-y-2 border-l pl-4">
            <div>
              <label className="text-xs text-gray-500">Reason (if rejecting)</label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="spam">Spam</SelectItem>
                  <SelectItem value="inappropriate">Inappropriate Content</SelectItem>
                  <SelectItem value="scam">Potential Scam</SelectItem>
                  <SelectItem value="wrong_category">Wrong Category</SelectItem>
                  <SelectItem value="duplicate">Duplicate Post</SelectItem>
                  <SelectItem value="policy_violation">Policy Violation</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <Textarea 
              placeholder="Notes (optional)"
              className="h-16 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            
            <div className="flex gap-2">
              <Button 
                size="sm" 
                className="flex-1 bg-green-600 hover:bg-green-700"
                onClick={() => onAction(post.id, 'approve', reason, notes)}
                disabled={loading}
              >
                <CheckCircle className="w-4 h-4 mr-1" />
                Approve
              </Button>
              <Button 
                size="sm" 
                variant="destructive"
                className="flex-1"
                onClick={() => onAction(post.id, 'reject', reason, notes)}
                disabled={loading || !reason}
              >
                <XCircle className="w-4 h-4 mr-1" />
                Reject
              </Button>
            </div>
            
            <Button 
              size="sm" 
              variant="outline"
              className="w-full text-orange-600 border-orange-200"
              onClick={() => onAction(post.id, 'flag', reason, notes)}
              disabled={loading}
            >
              <Flag className="w-4 h-4 mr-1" />
              Flag for Review
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default function CommunityModerationDashboard() {
  const { country } = useCountry();
  const [activeTab, setActiveTab] = useState('queue');
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState({ posts: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: 'pending',
    category: 'all',
    risk_level: 'all'
  });
  
  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/community/admin/moderation/stats?country=${country}`);
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  // Fetch moderation queue
  const fetchQueue = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        country,
        status: filters.status,
        page: 1,
        limit: 20
      });
      if (filters.category && filters.category !== 'all') params.append('category', filters.category);
      if (filters.risk_level && filters.risk_level !== 'all') params.append('risk_level', filters.risk_level);
      
      const response = await fetch(`${API_URL}/api/community/admin/moderation/queue?${params}`);
      const data = await response.json();
      setQueue(data);
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle moderation action
  const handleAction = async (postId, action, reason, notes) => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/community/admin/posts/${postId}/moderate?country=${country}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          reason: reason || undefined,
          notes: notes || undefined,
          moderator_id: 'admin' // In real app, get from auth
        })
      });
      
      if (response.ok) {
        // Refresh data
        fetchStats();
        fetchQueue();
      }
    } catch (error) {
      console.error('Failed to moderate post:', error);
    } finally {
      setActionLoading(false);
    }
  };
  
  useEffect(() => {
    fetchStats();
    fetchQueue();
  }, [country, filters]);
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              Community Moderation
            </h1>
            <p className="text-gray-600">Review and moderate community posts</p>
          </div>
          <Button onClick={() => { fetchStats(); fetchQueue(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats Grid */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard 
              title="Pending Review" 
              value={stats.pending_count} 
              icon={Clock}
              color="text-yellow-600"
            />
            <StatCard 
              title="Flagged Posts" 
              value={stats.flagged_count} 
              icon={Flag}
              color="text-orange-600"
            />
            <StatCard 
              title="Approved Today" 
              value={stats.approved_today} 
              icon={CheckCircle}
              color="text-green-600"
            />
            <StatCard 
              title="High Risk" 
              value={stats.by_risk_level?.high || 0} 
              icon={AlertTriangle}
              color="text-red-600"
            />
          </div>
        )}
        
        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="queue" className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Moderation Queue
              {stats?.pending_count > 0 && (
                <Badge className="ml-1 bg-yellow-500">{stats.pending_count}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="flagged" className="flex items-center gap-2">
              <Flag className="w-4 h-4" />
              Flagged
              {stats?.flagged_count > 0 && (
                <Badge className="ml-1 bg-orange-500">{stats.flagged_count}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="stats" className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Statistics
            </TabsTrigger>
          </TabsList>
          
          {/* Queue Tab */}
          <TabsContent value="queue">
            {/* Filters */}
            <Card className="mb-6">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>
                  
                  <Select 
                    value={filters.status} 
                    onValueChange={(v) => setFilters({...filters, status: v})}
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="flagged">Flagged</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={filters.category} 
                    onValueChange={(v) => setFilters({...filters, category: v})}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="ride-sharing">Ride Sharing</SelectItem>
                      <SelectItem value="classes-workshops">Classes</SelectItem>
                      <SelectItem value="lost-found">Lost & Found</SelectItem>
                      <SelectItem value="volunteers">Volunteers</SelectItem>
                      <SelectItem value="charity-donations">Charity</SelectItem>
                      <SelectItem value="community-help">Community Help</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={filters.risk_level} 
                    onValueChange={(v) => setFilters({...filters, risk_level: v})}
                  >
                    <SelectTrigger className="w-36">
                      <SelectValue placeholder="All Risk" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Risk</SelectItem>
                      <SelectItem value="high">High Risk</SelectItem>
                      <SelectItem value="medium">Medium Risk</SelectItem>
                      <SelectItem value="low">Low Risk</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
            
            {/* Posts List */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
              </div>
            ) : queue.posts.length > 0 ? (
              <>
                <p className="text-sm text-gray-500 mb-4">
                  Showing {queue.posts.length} of {queue.total} posts
                </p>
                {queue.posts.map(post => (
                  <ModerationPostCard 
                    key={post.id} 
                    post={post} 
                    onAction={handleAction}
                    loading={actionLoading}
                  />
                ))}
              </>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-4" />
                  <h3 className="text-xl font-bold">All Clear!</h3>
                  <p className="text-gray-500">No posts pending review</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Flagged Tab */}
          <TabsContent value="flagged">
            <Alert className="mb-6">
              <AlertTriangle className="w-4 h-4" />
              <AlertDescription>
                These posts have been flagged by users or the auto-moderation system. Please review carefully.
              </AlertDescription>
            </Alert>
            
            {/* Same list but with flagged filter */}
            <p className="text-gray-500 text-center py-8">
              Switch to Flagged filter in the queue to review flagged posts.
            </p>
          </TabsContent>
          
          {/* Stats Tab */}
          <TabsContent value="stats">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By Category */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.by_category && Object.entries(stats.by_category).map(([cat, count]) => {
                    const Icon = CATEGORY_ICONS[cat] || Shield;
                    return (
                      <div key={cat} className="flex items-center justify-between py-2 border-b last:border-0">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-500" />
                          <span className="capitalize">{cat.replace(/-/g, ' ')}</span>
                        </div>
                        <Badge variant={count > 0 ? 'default' : 'outline'}>{count}</Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
              
              {/* By Risk Level */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending by Risk Level</CardTitle>
                </CardHeader>
                <CardContent>
                  {stats?.by_risk_level && Object.entries(stats.by_risk_level).map(([level, count]) => (
                    <div key={level} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        <div className={`w-3 h-3 rounded-full ${
                          level === 'high' ? 'bg-red-500' : 
                          level === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                        <span className="capitalize">{level} Risk</span>
                      </div>
                      <Badge className={RISK_COLORS[level]}>{count}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
              
              {/* Today's Activity */}
              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Today's Moderation Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div className="p-4 bg-green-50 rounded-lg">
                      <p className="text-3xl font-bold text-green-600">{stats?.approved_today || 0}</p>
                      <p className="text-sm text-green-700">Approved</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg">
                      <p className="text-3xl font-bold text-red-600">{stats?.rejected_today || 0}</p>
                      <p className="text-sm text-red-700">Rejected</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg">
                      <p className="text-3xl font-bold text-yellow-600">{stats?.pending_count || 0}</p>
                      <p className="text-sm text-yellow-700">Still Pending</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
